import { Download, Upload, RefreshCw, CheckCircle } from 'lucide-react';

export const BackupTab = ({
  isArabic,
  onDownloadBackup,
  onRestoreBackup,
  backupLoading,
  restoreLoading,
  lastBackup
}) => {
  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (file) {
      await onRestoreBackup(file);
      e.target.value = '';
    }
  };

  return (
    <div className="p-6">
      <div className="flex flex-col sm:flex-row gap-4 justify-between items-start sm:items-center mb-6">
        <h2 className="text-2xl font-bold text-slate-900">
          {isArabic ? 'النسخ الاحتياطي والاستعادة' : 'Backup & Restore'}
        </h2>
      </div>

      <div className="grid md:grid-cols-2 gap-6 max-w-4xl">
        {/* Download Backup */}
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <div className="flex items-start gap-4 mb-6">
            <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
              <Download className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-900">
                {isArabic ? 'تحميل نسخة احتياطية' : 'Download Backup'}
              </h3>
              <p className="text-slate-500 text-sm mt-1">
                {isArabic 
                  ? 'قم بتحميل نسخة احتياطية من جميع البيانات'
                  : 'Download a backup of all your data'}
              </p>
            </div>
          </div>

          <button
            onClick={onDownloadBackup}
            disabled={backupLoading}
            className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors disabled:opacity-50 w-full justify-center"
            data-testid="download-backup-btn"
          >
            {backupLoading ? (
              <>
                <RefreshCw className="w-5 h-5 animate-spin" />
                {isArabic ? 'جاري التحميل...' : 'Downloading...'}
              </>
            ) : (
              <>
                <Download className="w-5 h-5" />
                {isArabic ? 'تحميل النسخة الاحتياطية' : 'Download Backup'}
              </>
            )}
          </button>

          {lastBackup && (
            <div className="mt-4 flex items-center gap-2 text-sm text-green-600">
              <CheckCircle className="w-4 h-4" />
              {isArabic ? 'آخر نسخة احتياطية:' : 'Last backup:'} {lastBackup}
            </div>
          )}
        </div>

        {/* Restore Backup */}
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <div className="flex items-start gap-4 mb-6">
            <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center">
              <Upload className="w-6 h-6 text-orange-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-900">
                {isArabic ? 'استعادة نسخة احتياطية' : 'Restore Backup'}
              </h3>
              <p className="text-slate-500 text-sm mt-1">
                {isArabic 
                  ? 'قم باستعادة البيانات من نسخة احتياطية سابقة'
                  : 'Restore your data from a previous backup'}
              </p>
            </div>
          </div>

          <label className="flex items-center gap-2 px-6 py-3 bg-orange-600 text-white rounded-xl hover:bg-orange-700 transition-colors cursor-pointer w-full justify-center">
            {restoreLoading ? (
              <>
                <RefreshCw className="w-5 h-5 animate-spin" />
                {isArabic ? 'جاري الاستعادة...' : 'Restoring...'}
              </>
            ) : (
              <>
                <Upload className="w-5 h-5" />
                {isArabic ? 'اختر ملف النسخة الاحتياطية' : 'Choose Backup File'}
              </>
            )}
            <input
              type="file"
              accept=".zip"
              onChange={handleFileSelect}
              className="hidden"
              disabled={restoreLoading}
              data-testid="restore-backup-input"
            />
          </label>

          <p className="mt-4 text-sm text-slate-500 text-center">
            {isArabic 
              ? 'يجب أن يكون الملف بصيغة .zip'
              : 'File must be in .zip format'}
          </p>
        </div>
      </div>

      {/* Warning */}
      <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-xl max-w-4xl">
        <p className="text-yellow-700 text-sm">
          <strong>{isArabic ? 'تحذير:' : 'Warning:'}</strong>{' '}
          {isArabic 
            ? 'استعادة نسخة احتياطية سيؤدي إلى استبدال جميع البيانات الحالية. تأكد من تحميل نسخة احتياطية قبل الاستعادة.'
            : 'Restoring a backup will replace all current data. Make sure to download a backup before restoring.'}
        </p>
      </div>
    </div>
  );
};

export default BackupTab;
